import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import type {
  AccountInfo,
  Configuration,
  RedirectRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { Effect, Either, Schedule } from "effect";
import { DateTime } from "luxon";

import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { silentRedirectUri } from "../msal-config.js";
import { getTokenExpiryTime } from "../token-utils.js";
import type { AuthService } from "./auth-service.interface.ts";

interface CreateMsalAuthServiceParams {
  msalConfig: Configuration;
  loginRequest: SilentRequest & RedirectRequest;
}

const getStillValidToken = (service: AuthService) => {
  if (
    service.accessToken &&
    service.expiresAt &&
    Date.now() < service.expiresAt
  ) {
    return service.accessToken;
  }

  return null;
};

const TOKEN_ACQUISITION_MAX_RETRIES = 3;
const TOKEN_ACQUISITION_RETRY_DELAY_MS = 500;

const isInteractionRequiredError = (error: unknown) =>
  error instanceof InteractionRequiredAuthError;

const tokenRetrySchedule = Schedule.exponential(
  TOKEN_ACQUISITION_RETRY_DELAY_MS,
  2
).pipe(Schedule.compose(Schedule.recurs(TOKEN_ACQUISITION_MAX_RETRIES)));

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export function createMsalAuthService({
  msalConfig,
  loginRequest,
}: CreateMsalAuthServiceParams) {
  let instance: PublicClientApplication | null = null;
  let initializationPromise: Promise<void> | null = null;
  let redirectPromise: Promise<void> | null = null;
  let tokenPromise: Promise<string | null> | null = null;
  let currentAccount: AccountInfo | null = null;

  const ensureInitialized = async () => {
    if (!instance) {
      instance = new PublicClientApplication(msalConfig);
      const msalInstance = instance;
      initializationPromise = msalInstance
        .initialize()
        .catch((error: unknown) => {
          instance = null;
          initializationPromise = null;
          throw error;
        });
    }

    const msalInstance = instance;
    if (!msalInstance || !initializationPromise) {
      throw new Error("MSAL failed to initialize.");
    }

    await initializationPromise;

    // handleRedirectPromise must complete before any token or login request.
    if (!redirectPromise) {
      redirectPromise = msalInstance
        .handleRedirectPromise()
        .then((result) => {
          if (result?.account) {
            currentAccount = result.account;
          }
        })
        .catch((error: unknown) => {
          redirectPromise = null;
          throw error;
        });
    }

    await redirectPromise;

    // Default to first cached account if none set
    if (!currentAccount) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && accounts[0]) {
        currentAccount = accounts[0];
      }
    }
  };

  const service: AuthService = {
    accessToken: null,
    expiresAt: null,

    getName() {
      return "Microsoft";
    },

    async getToken() {
      if (
        service.accessToken &&
        service.expiresAt &&
        Date.now() <
          DateTime.fromMillis(service.expiresAt)
            .minus({ minutes: 5 })
            .toMillis()
      ) {
        return service.accessToken;
      }

      if (tokenPromise) {
        return tokenPromise;
      }

      tokenPromise = (async () => {
        await ensureInitialized();

        if (!currentAccount) {
          recordErrorInSentry(
            new Error("MSAL: no account found for token acquisition")
          );
          return getStillValidToken(service);
        }

        const acquisition = await Effect.runPromise(
          Effect.either(
            Effect.retry(
              Effect.gen(function* acquireTokenSilently() {
                const msalInstance = instance;
                const account = currentAccount;

                if (!msalInstance || !account) {
                  return yield* Effect.fail(
                    new Error("MSAL instance or account is unavailable")
                  );
                }

                const result = yield* Effect.tryPromise({
                  catch: (error) => error,
                  try: () =>
                    msalInstance.acquireTokenSilent({
                      ...loginRequest,
                      account,
                      cacheLookupPolicy:
                        CacheLookupPolicy.AccessTokenAndRefreshToken,
                      redirectUri: silentRedirectUri,
                    }),
                });

                if (!result?.accessToken) {
                  return yield* Effect.fail(
                    new Error(
                      "MSAL acquireTokenSilent returned no access token"
                    )
                  );
                }

                service.accessToken = result.accessToken;
                service.expiresAt = getTokenExpiryTime(result.accessToken);

                return result.accessToken;
              }),
              {
                schedule: tokenRetrySchedule,
                while: (error) => !isInteractionRequiredError(error),
              }
            )
          )
        );

        if (Either.isLeft(acquisition)) {
          recordErrorInSentry(toError(acquisition.left));
          return getStillValidToken(service);
        }

        return acquisition.right;
      })();

      try {
        return await tokenPromise;
      } finally {
        tokenPromise = null;
      }
    },

    async login() {
      await ensureInitialized();

      await instance?.loginRedirect(loginRequest);
    },
  };

  return service;
}
